import { useNavigate } from 'react-router-dom'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import { MoreVertical, Pencil, Trash2 } from 'lucide-react'
import { Artwork } from '@/types/entities'
import { StatusBadge } from '@/components/common/data-display/StatusBadge'
import { artworkApi } from '../api/artworkApi'
import { truncate } from '@/lib/utils'
import { Pagination } from '@/components/common/navigation/Pagination'
import { useDeleteArtwork } from '../hooks/useArtworks'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useState } from 'react'

interface ArtworkTableProps {
  artworks: Artwork[]
  /**
   * Units and commission per artwork, when the caller has them.
   *
   * Optional because the same table renders Draft and Pending, where a sales column would be
   * three empty cells. Published is the tab where "which of these actually sells" is the question
   * the screen exists to answer.
   */
  sales?: Map<number, { units: number; commission: number }>
  page: number
  pageSize: number
  total: number
  onPageChange: (page: number) => void
  onPageSizeChange: (pageSize: number) => void
}

export function ArtworkTable({
  artworks,
  sales,
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
}: ArtworkTableProps) {
  const navigate = useNavigate()
  const { mutate: deleteArtwork } = useDeleteArtwork()
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [artworkToDelete, setArtworkToDelete] = useState<number | null>(null)

  const handleDelete = (artworkId: number) => {
    setArtworkToDelete(artworkId)
    setDeleteDialogOpen(true)
  }

  const confirmDelete = () => {
    if (artworkToDelete) {
      deleteArtwork(artworkToDelete)
      setDeleteDialogOpen(false)
      setArtworkToDelete(null)
    }
  }

  return (
    <div className="space-y-4">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-20">Image</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Description</TableHead>
              {sales && <TableHead className="w-24 text-right">Sold</TableHead>}
              {sales && <TableHead className="w-28 text-right">Earned</TableHead>}
              <TableHead className="w-32">Status</TableHead>
              <TableHead className="w-20"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {artworks.map((artwork) => (
              <TableRow
                key={artwork.id}
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => navigate(`/app/artworks/${artwork.id}`)}
              >
                <TableCell>
                  <img
                    src={artworkApi.getArtworkImageUrl(artwork.id)}
                    alt={artwork.name}
                    className="w-16 h-16 object-cover rounded"
                    onError={(e) => {
                      // Prevent infinite loop by checking if we're already showing the fallback
                      if (e.currentTarget.src.includes('placeholder-image.svg')) {
                        return;
                      }
                      e.currentTarget.src = '/placeholder-image.svg'
                    }}
                  />
                </TableCell>
                <TableCell className="font-medium">{artwork.name}</TableCell>
                <TableCell className="max-w-md text-muted-foreground">
                  {truncate(artwork.description || '', 100)}
                </TableCell>
                {sales && (
                  <TableCell className="text-right tabular-nums">
                    {sales.get(Number(artwork.id))?.units ?? 0}
                  </TableCell>
                )}
                {sales && (
                  <TableCell className="text-right tabular-nums">
                    {/* A piece that has never sold shows a dash rather than $0.00 — zero earned
                        and never offered look the same in currency, and they are not the same. */}
                    {sales.get(Number(artwork.id))
                      ? `$${(sales.get(Number(artwork.id))!.commission).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                      : <span className="text-muted-foreground">—</span>}
                  </TableCell>
                )}
                <TableCell>
                  <StatusBadge status={artwork.status} needsReview={artwork.reviewHold} />
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={() => navigate(`/app/artworks/${artwork.id}`)}
                      >
                        <Pencil className="mr-2 h-4 w-4" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleDelete(artwork.id)}
                        className="text-destructive"
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {total > pageSize && (
        <Pagination
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete your artwork.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

